// Shared SEO + AEO/GEO (Answer Engine Optimization) utilities.
//
// All site pages call into these helpers so we get consistent meta tags,
// canonical links, JSON-LD schemas, and a single source of truth for the
// brand entity. Designed for the AI Overviews / Perplexity / ChatGPT Search
// era — pages are structured to be cited, not just crawled.

export const ORG = {
  name: "CFO Grade",
  legalName: "CFO Grade",
  url: "https://cfograde.com",
  logo: "https://cfograde.com/og-cfograde.png",
  // One-sentence entity definition that gets embedded into every page's
  // schema. This is how LLMs build the entity graph for our brand.
  description:
    "CFO Grade is a free financial health analyzer for small and mid-sized businesses. It computes 24+ ratios (EBITDA, DSCR, margins, working capital), assigns a letter grade A+ to F, and benchmarks against your industry and region.",
  sameAs: [],
  foundingDate: "2025",
};

export const AUTHOR = {
  name: "CFO Grade Research Team",
  url: "https://cfograde.com/learn",
};

const DEFAULT_REVIEWED_AT = "2026-02-15";

export function upsertMeta(attr, key, value) {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

export function upsertCanonical(href) {
  if (typeof document === "undefined") return;
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function upsertJsonLd(id, json) {
  if (typeof document === "undefined") return;
  // Remove previous instance to avoid stale schemas accumulating on navigation
  const old = document.getElementById(id);
  if (old) old.remove();
  const el = document.createElement("script");
  el.setAttribute("type", "application/ld+json");
  el.setAttribute("id", id);
  el.textContent = JSON.stringify(json);
  document.head.appendChild(el);
}

// Clean up any previously injected JSON-LD blocks owned by us. Call this on
// page mount to avoid blocks from the previous route leaking into the current
// page's schema graph (which confuses Google's structured data parser).
export function clearOwnedJsonLd(ids) {
  if (typeof document === "undefined") return;
  for (const id of ids) {
    const el = document.getElementById(id);
    if (el) el.remove();
  }
}

export function setStandardMeta({ title, description, url, image }) {
  document.title = title;
  upsertMeta("name", "description", description);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:type", "article");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  if (image) upsertMeta("property", "og:image", image);
  upsertCanonical(url);
}

// --- Schema builders ---------------------------------------------------------

export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${ORG.url}#org`,
    name: ORG.name,
    legalName: ORG.legalName,
    url: ORG.url,
    logo: ORG.logo,
    description: ORG.description,
    foundingDate: ORG.foundingDate,
  };
}

export function buildArticleSchema({ title, description, url, reviewedAt }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url,
    mainEntityOfPage: url,
    image: ORG.logo,
    author: {
      "@type": "Organization",
      name: AUTHOR.name,
      url: AUTHOR.url,
    },
    publisher: {
      "@type": "Organization",
      name: ORG.name,
      url: ORG.url,
      logo: { "@type": "ImageObject", url: ORG.logo },
    },
    datePublished: "2025-12-01",
    dateModified: reviewedAt || DEFAULT_REVIEWED_AT,
  };
}

export function buildDefinedTermSchema({ name, description, slug }) {
  return {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    name,
    description,
    inDefinedTermSet: {
      "@type": "DefinedTermSet",
      name: "CFO Grade Financial Ratios Glossary",
      url: `${ORG.url}/learn`,
    },
    url: `${ORG.url}/learn/${slug}`,
    termCode: slug,
  };
}

export function buildFaqSchema(qas) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qas.map((q) => ({
      "@type": "Question",
      name: q.q,
      acceptedAnswer: { "@type": "Answer", text: q.a },
    })),
  };
}

// BreadcrumbList — improves SERP appearance and helps AI crawlers see
// the hierarchy from `/` → `/learn` → `/learn/{slug}` (or any nested
// taxonomy). Pass an ordered array of { name, url } pairs starting at
// the site root.
export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// --- People-Also-Ask defaults -----------------------------------------------

// Derive 4 sensible Q&As from a concept's existing data so every glossary page
// gets a high-quality PAA block automatically. Concepts that hand-author a
// `paa` array override this entirely.
export function derivePaaForConcept(concept) {
  const tightDef =
    concept.short.length > 220 ? concept.short.slice(0, 217) + "…" : concept.short;
  const formulaAnswer = concept.formulaPlain || concept.formula;
  const benchPick = (concept.benchmarks || []).find((b) => b.tone === "good") ||
    (concept.benchmarks || [])[0];
  const goodAnswer = benchPick
    ? `A healthy ${concept.title.toLowerCase()} is typically around ${benchPick.value} — ${benchPick.label.toLowerCase()}. Specific targets vary by industry and stage; check our benchmarks above for your sector.`
    : `A healthy ${concept.title.toLowerCase()} varies by industry. Run your numbers through CFO Grade for a benchmark tuned to your sector.`;
  const whyAnswer = (concept.why || "").split(". ").slice(0, 2).join(". ") + ".";

  return [
    { q: `What is ${concept.title}?`, a: tightDef },
    { q: `How is ${concept.title} calculated?`, a: formulaAnswer },
    { q: `What is a good ${concept.title}?`, a: goodAnswer },
    { q: `Why does ${concept.title} matter?`, a: whyAnswer },
  ];
}

// Same idea for industry pages — generate a defensible default PAA so every
// /for/* page is AEO-ready without hand-authoring 44 individual answers.
export function derivePaaForIndustry(industry) {
  const benchOne = industry.benchmark?.[0];
  const benchTwo = industry.benchmark?.[1];
  return [
    {
      q: `What financial metrics should ${industry.sub.toLowerCase()} owners track?`,
      a: `${industry.sub} owners should track ${(industry.benchmark || [])
        .map((b) => b.k)
        .join(", ")} — the metrics lenders, investors, and acquirers use to underwrite this sector.`,
    },
    {
      q: `What is a healthy ${benchOne?.k?.toLowerCase() || "margin"} for ${industry.sub.toLowerCase()}?`,
      a: benchOne
        ? `For ${industry.sub.toLowerCase()}, a healthy ${benchOne.k.toLowerCase()} is around ${benchOne.v}. CFO Grade benchmarks your number against this range and assigns a letter grade.`
        : "It varies by sub-segment — run your P&L through CFO Grade to see how you compare.",
    },
    {
      q: `How do lenders evaluate ${industry.sub.toLowerCase()}?`,
      a: benchTwo
        ? `Lenders for ${industry.sub.toLowerCase()} focus heavily on ${benchTwo.k.toLowerCase()} (target: ${benchTwo.v}), DSCR (debt service coverage), and current ratio. CFO Grade computes all three from your P&L and balance sheet.`
        : "Lenders focus on DSCR (debt service coverage), debt-to-EBITDA, and current ratio. CFO Grade computes all three from your P&L and balance sheet.",
    },
    {
      q: `Can I use CFO Grade for free?`,
      a: "Yes. The core analysis — 24+ ratios, letter grade, peer benchmarks, and a plain-English memo — is free with no signup. Pro features (PDF export, the What-If sandbox, multi-period trends) are a one-time $29 Pro Pass.",
    },
  ];
}
